export const STRONG_PASSWORD_MIN_LENGTH = 12;

export const STRONG_PASSWORD_REQUIREMENTS =
  `Password must be at least ${STRONG_PASSWORD_MIN_LENGTH} characters and include uppercase, lowercase, number, and special character. Avoid common or easily guessed patterns.`;

const COMMON_PASSWORDS = new Set([
  'password',
  'password123',
  'password123!',
  'qwerty123',
  'qwerty123!',
  '12345678',
  '123456789',
  '1234567890',
  'admin123',
  'welcome123',
  'letmein123',
  'iloveyou123'
]);

interface PasswordValidationContext {
  email?: string;
  displayName?: string;
}

export interface PasswordValidationResult {
  isValid: boolean;
  message?: string;
}

const hasSequentialRun = (password: string): boolean => {
  const lower = password.toLowerCase();
  let increasingRun = 1;
  let decreasingRun = 1;

  for (let i = 1; i < lower.length; i += 1) {
    const current = lower.charCodeAt(i);
    const previous = lower.charCodeAt(i - 1);
    const isDigit = current >= 48 && current <= 57 && previous >= 48 && previous <= 57;
    const isLetter = current >= 97 && current <= 122 && previous >= 97 && previous <= 122;

    if ((isDigit || isLetter) && current === previous + 1) {
      increasingRun += 1;
    } else {
      increasingRun = 1;
    }

    if ((isDigit || isLetter) && current === previous - 1) {
      decreasingRun += 1;
    } else {
      decreasingRun = 1;
    }

    if (increasingRun >= 4 || decreasingRun >= 4) {
      return true;
    }
  }

  return false;
};

export const validateStrongPassword = (
  password: string,
  context?: PasswordValidationContext
): PasswordValidationResult => {
  if (password.length < STRONG_PASSWORD_MIN_LENGTH) {
    return {
      isValid: false,
      message: `Password must be at least ${STRONG_PASSWORD_MIN_LENGTH} characters long.`
    };
  }

  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasDigit = /\d/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);

  if (!hasUppercase || !hasLowercase || !hasDigit || !hasSpecial) {
    return {
      isValid: false,
      message: 'Password must include uppercase, lowercase, number, and special character.'
    };
  }

  if (/\s/.test(password)) {
    return {
      isValid: false,
      message: 'Password must not contain spaces.'
    };
  }

  if (COMMON_PASSWORDS.has(password.toLowerCase())) {
    return {
      isValid: false,
      message: 'Please choose a less common password.'
    };
  }

  if (/(.)\1{3,}/.test(password)) {
    return {
      isValid: false,
      message: 'Password must not include repeated characters like "aaaa" or "1111".'
    };
  }

  if (hasSequentialRun(password)) {
    return {
      isValid: false,
      message: 'Password must not include sequential patterns like "abcd" or "1234".'
    };
  }

  const emailLocalPart = context?.email?.split('@')[0]?.toLowerCase() || '';
  const normalizedName = (context?.displayName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const normalizedPassword = password.toLowerCase();

  if (emailLocalPart && emailLocalPart.length >= 3 && normalizedPassword.includes(emailLocalPart)) {
    return {
      isValid: false,
      message: 'Password must not contain your email name.'
    };
  }

  if (normalizedName && normalizedName.length >= 3 && normalizedPassword.includes(normalizedName)) {
    return {
      isValid: false,
      message: 'Password must not contain your name.'
    };
  }

  return { isValid: true };
};
